import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, resetState } from '../GlobalAction';
import { AssetType, IActivity, IStepMediaAsset } from '../activity/activitySlice';
import { IAddActivityResponse } from '../activity/activitySaga';
import { AdvanceActivityStepType } from '../../types/activity';

export interface CreateRenameFolderPayload {
  folderName: string;
}

export interface AddActivityPayload {
  folderName: string;
}

export interface IAddActivityLibraryPayload {
  activityInput: IActivity;
  stepMediaAssets: IStepMediaAsset[];
}

export interface IDeleteActivityLPayload {
  id: string;
  coachId: string;
  isMoveToTrash?: boolean;
  folderId?: string | null;
  isInsideFolder?: boolean;
}

export interface IRestoreActivityLPayload {
  id: string;
  coachId: string;
}

export interface IAddActivityLibraryGraphQLResponse {
  addActivityLibrary: IAddActivityResponse;
}

export interface IEditActivityLibraryGraphQLResponse {
  editActivityLibrary: IAddActivityResponse;
}

export interface CreateFolderPayload {
  name: string;
  coachId: string;
}

export interface RenameFolderPayload {
  name: string;
  coachId: string;
  id: string;
}

export enum IFolderType {
  NORMAL = 'NORMAL',
  TRASH = 'TRASH',
}

export interface FolderWithActivity {
  id: string;
  name: string;
  folderType: IFolderType;
}

export interface ActivityLibraryStep {
  id: string;
  activityLibraryId: string;
  stepOrder: number;
  text: string;
  asset: string;
  assetType: AssetType;
  isAdvanceStep: boolean;
  advanceStepType: AdvanceActivityStepType;
  advanceStepData: string[];
}

export interface ActivityLibraries {
  id: string;
  name: string;
  objective: string;
  isAdvanceActivity: boolean;
  ActivityLibraryStep: ActivityLibraryStep[];
}

export interface FolderAndActivityResponse {
  folders: FolderWithActivity[];
  activityLibraries: ActivityLibraries[];
}

export interface IGetUserFoldersAndActivitiesGraphQLResponse {
  getUserFoldersAndActivities: FolderAndActivityResponse;
}

export interface ActivitiesInsideFolder {
  totalActivityCount: number;
  activity: ActivityLibraries[];
}

export interface IGetActivitiesInsideFolderGraphQLResponse {
  getActivityLibraries: ActivitiesInsideFolder;
}

export enum ActivityLibraryBlocks {
  ACTIVITY_LIBRARY = 'activityLibrary',
  SINGLE_FOLDER = 'singleFolder',
}

export interface IDeleteFolderLPayload {
  folderId: string;
  coachId: string;
  isEmptyTrash?: boolean;
}

export interface ICheckActivityLibraryExistenceResponse {
  checkActivityLibraryExistence: {
    success: boolean;
    message: string;
  };
}

export interface IDeleteFolderGraphQLResponse {
  deleteFolder: {
    id: string;
  };
}

export interface IRenameFolderGraphQLResponse {
  editFolder: {
    id: string;
    name: string;
  };
}

interface IActivityLibraryState {
  isCreateFolderModalOpen: boolean;
  createNewFolderLoading: boolean;
  createNewFolderError: string;
  isRenameFolderSuccess: boolean;
  renameFolderLoading: boolean;
  renameFolderError: string;
  addActivityLoading: boolean;
  editActivityLoading: boolean;
  isAddActivityModalOpen: boolean;
  isEditActivityModalOpen: boolean;
  addActivityError: string;
  isAddActivitySuccess: boolean;
  isAEditActivitySuccess: boolean;
  editActivityError: string;
  getUserFoldersAndActivitiesLoading: boolean;
  userFoldersAndActivities: FolderAndActivityResponse;
  folder: {
    name: string;
    id: string;
    folderType: IFolderType;
  };
  getActivitiesInsideFolderLoading: boolean;
  activitiesInsideFolder: ActivitiesInsideFolder;
  block: ActivityLibraryBlocks;
  deleteActivityLoading: boolean;
  isDeleteActivitySuccess: boolean;
  deleteFolderLoading: boolean;
  isDeleteFolderSuccess: boolean;
  restoreActivityLoading: boolean;
  restoreActivitySuccess: boolean;
  permenantlyDeleteActivitySuccess: boolean;
  activityExistenceStatus: boolean | null;
  userFolders: FolderWithActivity[];
}

const initialState: IActivityLibraryState = {
  isCreateFolderModalOpen: false,
  createNewFolderLoading: false,
  createNewFolderError: '',
  isRenameFolderSuccess: false,
  renameFolderLoading: false,
  renameFolderError: '',
  addActivityLoading: false,
  isAddActivityModalOpen: false,
  addActivityError: '',
  isAddActivitySuccess: false,
  editActivityLoading: false,
  isEditActivityModalOpen: false,
  isAEditActivitySuccess: false,
  editActivityError: '',
  getUserFoldersAndActivitiesLoading: false,
  userFoldersAndActivities: {
    folders: [],
    activityLibraries: [],
  },
  folder: {
    name: '',
    id: '',
    folderType: IFolderType.NORMAL,
  },
  getActivitiesInsideFolderLoading: false,
  activitiesInsideFolder: {
    totalActivityCount: 0,
    activity: [],
  },
  block: ActivityLibraryBlocks.ACTIVITY_LIBRARY,
  deleteActivityLoading: false,
  isDeleteActivitySuccess: false,
  deleteFolderLoading: false,
  isDeleteFolderSuccess: false,
  restoreActivityLoading: false,
  restoreActivitySuccess: false,
  permenantlyDeleteActivitySuccess: false,
  activityExistenceStatus: null,
  userFolders: [],
};

const activityLibrarySlice = createSlice({
  name: 'activityLibrary',
  initialState,
  reducers: {
    setCreateFolderModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateFolderModalOpen = action.payload;
    },
    createNewFolder: (state, _action: PayloadAction<CreateFolderPayload>) => {
      state.createNewFolderLoading = true;
    },
    createNewFolderSuccess: (state) => {
      state.createNewFolderLoading = false;
      state.isCreateFolderModalOpen = false;
    },
    createNewFolderFailure: (state, action: PayloadAction<string>) => {
      state.createNewFolderLoading = false;
      state.createNewFolderError = action.payload;
    },
    renameFolder: (state, _action: PayloadAction<RenameFolderPayload>) => {
      state.renameFolderLoading = true;
    },
    renameFolderSuccess: (state) => {
      state.renameFolderLoading = false;
      state.isRenameFolderSuccess = true;
    },
    closeRenameFolderOnSuccess: (state) => {
      state.isRenameFolderSuccess = false;
    },
    renameFolderFailure: (state, action: PayloadAction<string>) => {
      state.renameFolderLoading = false;
      state.renameFolderError = action.payload;
    },
    addActivity: (state, _action: PayloadAction<IAddActivityLibraryPayload>) => {
      state.addActivityLoading = true;
    },
    addActivitySuccess: (state) => {
      state.addActivityLoading = false;
      state.isAddActivityModalOpen = false;
      state.isAddActivitySuccess = true;
    },
    closeAddActivityModalOnSuccess: (state) => {
      state.isAddActivitySuccess = false;
    },
    addActivityFailure: (state, action: PayloadAction<string>) => {
      state.addActivityLoading = false;
      state.addActivityError = action.payload;
    },
    editActivity: (state, _action: PayloadAction<IAddActivityLibraryPayload>) => {
      state.editActivityLoading = true;
    },
    editActivitySuccess: (state) => {
      state.editActivityLoading = false;
      state.isEditActivityModalOpen = false;
      state.isAEditActivitySuccess = true;
    },
    closeEditActivityModalOnSuccess: (state) => {
      state.isAEditActivitySuccess = false;
    },
    editActivityFailure: (state, action: PayloadAction<string>) => {
      state.editActivityLoading = false;
      state.editActivityError = action.payload;
    },
    getUserFoldersAndActivities: (
      state,
      _action: PayloadAction<{ coachId: string; filterValue?: string }>
    ) => {
      state.getUserFoldersAndActivitiesLoading = true;
    },
    getUserFoldersAndActivitiesSuccess: (state, action) => {
      state.getUserFoldersAndActivitiesLoading = false;
      state.userFoldersAndActivities = action.payload;
    },
    getUserFoldersAndActivitiesFailure: (state) => {
      state.getUserFoldersAndActivitiesLoading = false;
    },
    saveFolder: (state, action) => {
      state.folder = action.payload;
      state.block = ActivityLibraryBlocks.SINGLE_FOLDER;
    },
    setActivityLibraryBlock: (state) => {
      state.block = ActivityLibraryBlocks.ACTIVITY_LIBRARY;
    },
    getActivitiesInsideFolder: (
      state,
      action: PayloadAction<{
        coachId: string;
        filterValue?: string;
        folderId: string;
        page: number;
        pageSize: number;
        isSearch?: boolean;
      }>
    ) => {
      state.getActivitiesInsideFolderLoading = true;
      if (action.payload.isSearch) {
        state.activitiesInsideFolder.activity = [];
      }
    },
    getActivitiesInsideFolderSuccess: (state, action: PayloadAction<ActivitiesInsideFolder>) => {
      state.getActivitiesInsideFolderLoading = false;
      state.activitiesInsideFolder.totalActivityCount = action.payload.totalActivityCount;
      state.activitiesInsideFolder.activity = [
        ...state.activitiesInsideFolder.activity,
        ...action.payload.activity,
      ];
    },
    clearActivitiesInsideFolder: (state) => {
      state.activitiesInsideFolder.totalActivityCount = 0;
      state.activitiesInsideFolder.activity = [];
    },
    clearFolder: (state) => {
      state.folder = { id: '', name: '', folderType: IFolderType.NORMAL };
    },
    getActivitiesInsideFolderFailure: (state) => {
      state.getActivitiesInsideFolderLoading = false;
    },
    deleteActivity: (state, _action: PayloadAction<IDeleteActivityLPayload>) => {
      state.deleteActivityLoading = true;
    },
    deleteActivitySuccess: (state) => {
      state.deleteActivityLoading = false;
      state.isDeleteActivitySuccess = true;
    },
    closeDeleteActivityModalOnSuccess: (state) => {
      state.isDeleteActivitySuccess = false;
    },
    deleteActivityFailure: (state) => {
      state.deleteActivityLoading = false;
    },
    deleteFolder: (state, _action: PayloadAction<IDeleteFolderLPayload>) => {
      state.deleteFolderLoading = true;
    },
    deleteFolderSuccess: (state) => {
      state.deleteFolderLoading = false;
      state.isDeleteFolderSuccess = true;
    },
    closeDeleteFolderModalOnSuccess: (state) => {
      state.isDeleteFolderSuccess = false;
    },
    deleteFolderFailure: (state) => {
      state.deleteFolderLoading = false;
    },
    restoreActivity: (state, _action: PayloadAction<IRestoreActivityLPayload>) => {
      state.restoreActivityLoading = true;
    },
    restoreActivitySuccess: (state) => {
      state.restoreActivityLoading = false;
      state.restoreActivitySuccess = true;
    },
    restoreActivityFailure: (state) => {
      state.restoreActivityLoading = false;
    },
    resetRestoreActivitySuccess: (state) => {
      state.restoreActivitySuccess = false;
    },
    permenantlyDeleteActivity: (state, _action: PayloadAction<{ id: string }>) => {
      state.deleteActivityLoading = true;
    },
    permenantlyDeleteActivitySuccess: (state) => {
      state.deleteActivityLoading = false;
      state.permenantlyDeleteActivitySuccess = true;
    },
    permenantlyDeleteActivityFailure: (state) => {
      state.deleteActivityLoading = false;
    },
    resetPermenantlyDeleteActivitySuccess: (state) => {
      state.permenantlyDeleteActivitySuccess = false;
    },
    checkActivityLibraryExistence: (
      state,
      _action: PayloadAction<{ activityTitle: string; activityId?: string }>
    ) => {
      state.activityExistenceStatus = null;
    },
    checkActivityLibraryExistenceSuccess: (state, action: PayloadAction<boolean>) => {
      state.activityExistenceStatus = action.payload;
    },
    checkActivityLibraryExistenceFailure: (state) => {
      state.activityExistenceStatus = null;
    },
    resetCheckActivityLibraryExistenceStatus: (state) => {
      state.activityExistenceStatus = null;
    },
    setUserFolders: (state, action: PayloadAction<FolderWithActivity[]>) => {
      state.userFolders = action.payload;
    },
    removeFolderFromUserFolders: (state, action: PayloadAction<string>) => {
      const index = state.userFolders.findIndex((v) => v.id === action.payload);
      state.userFolders.splice(index, 1);
    },
    updateFolderNameUserFolders: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const index = state.userFolders.findIndex((v) => v.id === action.payload.id);
      state.userFolders[index].name = action.payload.name;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, () => initialState);
    builder.addCase(logout, () => initialState);
  },
});

export const {
  addActivity,
  addActivitySuccess,
  addActivityFailure,
  closeAddActivityModalOnSuccess,
  editActivity,
  editActivitySuccess,
  closeEditActivityModalOnSuccess,
  editActivityFailure,
  getUserFoldersAndActivities,
  getUserFoldersAndActivitiesSuccess,
  getUserFoldersAndActivitiesFailure,
  createNewFolderSuccess,
  createNewFolderFailure,
  renameFolderSuccess,
  renameFolderFailure,
  createNewFolder,
  renameFolder,
  saveFolder,
  getActivitiesInsideFolder,
  getActivitiesInsideFolderSuccess,
  getActivitiesInsideFolderFailure,
  clearActivitiesInsideFolder,
  setActivityLibraryBlock,
  clearFolder,
  setCreateFolderModalOpen,
  deleteActivity,
  deleteActivitySuccess,
  closeDeleteActivityModalOnSuccess,
  deleteActivityFailure,
  deleteFolder,
  deleteFolderSuccess,
  closeDeleteFolderModalOnSuccess,
  deleteFolderFailure,
  closeRenameFolderOnSuccess,
  restoreActivity,
  restoreActivitySuccess,
  restoreActivityFailure,
  resetRestoreActivitySuccess,
  permenantlyDeleteActivity,
  permenantlyDeleteActivitySuccess,
  permenantlyDeleteActivityFailure,
  resetPermenantlyDeleteActivitySuccess,
  checkActivityLibraryExistence,
  checkActivityLibraryExistenceSuccess,
  checkActivityLibraryExistenceFailure,
  resetCheckActivityLibraryExistenceStatus,
  setUserFolders,
  removeFolderFromUserFolders,
  updateFolderNameUserFolders,
} = activityLibrarySlice.actions;

export default activityLibrarySlice.reducer;
